// src/pages/home/components/RoutineScore.tsx
import React, { useMemo } from "react";

type Props = {
  value: number;
  min?: number;
  max?: number;
  title?: string;
  badgeText?: string;
  formatLabel?: (n: number) => string;
};

const polar = (cx: number, cy: number, r: number, deg: number) => {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};
const arcPath = (
  cx: number,
  cy: number,
  r: number,
  start: number,
  end: number
) => {
  const s = polar(cx, cy, r, end);
  const e = polar(cx, cy, r, start);
  const large = end - start <= 180 ? 0 : 1;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 0 ${e.x} ${e.y}`;
};
const clamp01 = (t: number) => Math.max(0, Math.min(1, t));

const RoutineScore: React.FC<Props> = ({
  value,
  min = 0,
  max = 100,
  title = "오늘 루틴 점수",
  badgeText = "건강 만렙! 완벽해요! 👍",
  formatLabel,
}) => {
  // 카드/게이지 치수
  const CARD_H = 221;
  const STROKE = 30;
  const SVG_W = 290,
    SVG_H = 190;

  // 원/호 좌표계
  const cx = SVG_W / 2;
  const cy = SVG_H * 0.9;
  const r = (SVG_W - STROKE - 12) / 2;

  // 값 → 각도
  const t = clamp01((value - min) / Math.max(1e-6, max - min));
  const start = -180,
    endTrack = 0,
    endProg = start + (endTrack - start) * t;

  // 경로
  const trackD = useMemo(
    () => arcPath(cx, cy, r, start, endTrack),
    [cx, cy, r, start]
  );
  const progD = useMemo(
    () => arcPath(cx, cy, r, start, endProg),
    [cx, cy, r, start, endProg]
  );

  // 끝점(라벨용)
  const minEnd = polar(cx, cy, r, start);
  // const maxEnd = polar(cx, cy, r, endTrack);

  // 라벨 x는 '안쪽'으로 살짝, y는 캡 바로 밑
  const LABEL_INSET = STROKE / 2 + 12;
  const minLabelX = polar(cx, cy, r - LABEL_INSET, start).x;
  const maxLabelX = polar(cx, cy, r - LABEL_INSET, endTrack).x;
  const labelY = minEnd.y + STROKE / 2 + 18;

  const display = Math.round(value);
  const fmt = (n: number) => (formatLabel ? formatLabel(n) : String(n));

  return (
    <div
      style={{
        width: "100%",
        height: "221px",
        borderRadius: 16,
        background: "#fff",
        boxShadow: "0 10px 26px rgba(0,0,0,0.10)",
        overflow: "hidden",
        position: "relative",
        padding: "19.5px 36.3px",
      }}
    >
      {/* 상단: 게이지 */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            height: "100%",
            position: "relative",
            top: "-25%",
          }}
        >
          <svg width={SVG_W} height={220} viewBox={`0 0 ${SVG_W} ${SVG_H}`}>
            <defs>
              <linearGradient id="gaugeGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#FFA44A" />
                <stop offset="100%" stopColor="#FF7A3D" />
              </linearGradient>
              <filter id="soft" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow
                  dx="0"
                  dy="2"
                  stdDeviation="2"
                  floodOpacity="0.18"
                />
              </filter>
            </defs>

            {/* 트랙/진행/노브 */}
            <path
              d={trackD}
              stroke="#E5E7EB"
              strokeWidth={STROKE}
              strokeLinecap="round"
              fill="none"
            />
            <path
              d={progD}
              stroke="url(#gaugeGrad)"
              strokeWidth={STROKE}
              strokeLinecap="round"
              fill="none"
              // style={{ filter: "url(#soft)" }}
            />
            {(() => {
              const k = polar(cx, cy, r, endProg);
              return <circle cx={k.x} cy={k.y} r={STROKE / 2} fill="#FF7A3D" />;
            })()}

            {/* 0 / 100 : 캡 바로 밑, 안쪽 */}
            <text
              x={minLabelX - 28}
              y={labelY}
              textAnchor="middle"
              fontSize={16}
              fill="#D1D5DB"
            >
              {fmt(min)}
            </text>
            <text
              x={maxLabelX + 29}
              y={labelY}
              textAnchor="middle"
              fontSize={16}
              fill="#D1D5DB"
            >
              {fmt(max)}
            </text>
          </svg>
        </div>

        {/* 중앙 값 */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "53%",
            transform: "translate(-50%, -50%)",
            display: "grid",
            placeItems: "center",
            pointerEvents: "none",
          }}
        >
          <div style={{ fontSize: 14, color: "#9AA0A6", marginBottom: 6 }}>
            {title}
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <div
              style={{
                fontSize: 48,
                fontWeight: 800,
                color: "#111827",
                lineHeight: 1,
              }}
            >
              {fmt(display)}
            </div>
            <div style={{ fontSize: 16, color: "#6B7280" }}>점</div>
          </div>
        </div>
        {/* 하단 배지 */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            height: CARD_H - 190,
            display: "grid",
            placeItems: "center",
          }}
        >
          <div
            style={{
              background: "#F5F5F5",
              color: "#525252",
              fontSize: 14,
              fontWeight: 500,
              padding: "6px 10px",
              borderRadius: 6,
              // boxShadow:
              //   "0 1px 0 rgba(255,255,255,0.6), 0 6px 18px rgba(0,0,0,0.06)",
              whiteSpace: "nowrap",
            }}
          >
            {badgeText}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoutineScore;
